"use client";

import { type HTMLArkProps, ark } from "@ark-ui/react";
import * as React from "react";
import type { ColorNames } from "../../tokens/index.js";

const defaultThemeValues: ThemeContextProps = {
  primaryColor: "primary-red",
  secondaryColor: "primary-blue",
  isRoot: false,
};

interface ThemeContextProps {
  primaryColor: ColorNames;
  secondaryColor: ColorNames;
  isRoot: boolean;
}

interface ThemeProviderProps
  extends HTMLArkProps<"div">,
    Partial<ThemeContextProps> {}

const ThemeContext = React.createContext<ThemeContextProps>(defaultThemeValues);
ThemeContext.displayName = "OneUIThemeContext";

const useThemeContext = (): ThemeContextProps => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a OneUIProvider");
  }
  return context;
};

const OneUIProvider = React.forwardRef<HTMLDivElement, ThemeProviderProps>(
  (props, ref) => {
    const {
      primaryColor = defaultThemeValues.primaryColor,
      secondaryColor = defaultThemeValues.secondaryColor,
      isRoot = defaultThemeValues.isRoot,
      ...rest
    } = props;

    const parentContext = React.useContext(ThemeContext);
    const root = isRoot ?? parentContext === undefined;

    const themeContextValue = React.useMemo(
      () => ({
        primaryColor,
        secondaryColor,
        isRoot: root,
      }),
      [primaryColor, secondaryColor, root],
    );

    return (
      <ThemeContext.Provider value={themeContextValue}>
        <ark.div
          ref={ref}
          data-is-root={root}
          data-primary-color={primaryColor}
          data-secondary-color={secondaryColor}
          {...rest}
        />
      </ThemeContext.Provider>
    );
  },
);

OneUIProvider.displayName = "OneUIProvider";

export { OneUIProvider, defaultThemeValues as defaultTheme, useThemeContext };
export type { ThemeContextProps };
